import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { gam } from "../lib/gamificationApi";
import { useAuth } from "../lib/AuthContext";
import { TopBar } from "../components/Navigation";
import LevelBadge from "../components/LevelBadge";
import PassportShowcase from "../components/PassportShowcase";
import { Mountain, ChevronRight } from "lucide-react";

export default function Passport() {
    const { userId } = useParams();
    const { user: me } = useAuth();
    const targetId = userId || me?.user_id;
    const [passport, setPassport] = useState(null);
    const [level, setLevel] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!targetId) return;
        let cancel = false;
        (async () => {
            setLoading(true);
            try {
                const [p, l, t] = await Promise.all([
                    gam.passport(targetId),
                    gam.level(targetId),
                    !userId || userId === me?.user_id ? gam.timeline(20) : Promise.resolve({ items: [] }),
                ]);
                if (cancel) return;
                setPassport(p);
                setLevel(l);
                setTimeline(t.items || []);
            } catch { /* ignore */ }
            finally { if (!cancel) setLoading(false); }
        })();
        return () => { cancel = true; };
    }, [targetId, userId, me?.user_id]);

    if (loading) {
        return (
            <div data-testid="passport-loading" className="max-w-5xl mx-auto p-6">
                <div className="h-8 w-64 bg-trail-tan animate-pulse rounded" />
            </div>
        );
    }

    return (
        <div data-testid="passport-page">
            <TopBar title="Adventure Passport" />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 space-y-6">
                <header className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-slate-gray">Build your adventure legacy</div>
                        <h1 className="font-display font-black text-3xl sm:text-4xl text-midnight-pine tracking-tight">Adventure Passport</h1>
                        <p className="text-sm text-slate-gray mt-1 max-w-xl">
                            A living record of where you've been, what you've done, and what you've helped others discover.
                        </p>
                    </div>
                    <Link
                        to="/challenges"
                        data-testid="passport-challenges-link"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-ember-orange text-white text-sm font-bold hover:bg-ember-dark active:scale-95 transition-all"
                    >
                        See challenges <ChevronRight className="w-4 h-4" />
                    </Link>
                </header>

                <LevelBadge levelData={level} variant="hero" linkable={false} />

                <PassportShowcase passport={passport} />

                {timeline.length > 0 && (
                    <section data-testid="xp-timeline" className="bg-snowcap border border-ridge-line rounded-xl p-5">
                        <h3 className="font-display font-bold text-base text-midnight-pine flex items-center gap-2 mb-3">
                            <Mountain className="w-4 h-4 text-ember-orange" /> Recent XP
                        </h3>
                        <ul className="space-y-2 text-sm">
                            {timeline.slice(0, 12).map((e) => (
                                <li key={e.id} className="flex items-center justify-between border-b border-ridge-line/50 pb-2 last:border-b-0">
                                    <span className="text-granite capitalize">{e.action.replace(/_/g, " ")}</span>
                                    <span className="text-xs text-slate-gray">
                                        {new Date(e.created_at).toLocaleDateString()} · <span className="font-bold text-ember-orange">+{e.xp} XP</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </div>
        </div>
    );
}
